import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Ellipsis, Folder, Folders } from 'lucide-react';
import { IconButton } from '@/components/ui/custom/IconButton';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	Dialog,
	DialogTitle,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTrigger,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectItem,
	SelectGroup,
	SelectLabel,
} from '@/components/ui/select';
import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { deleteFolderFromAllCustomerConversations } from '@/actions/webchat';
import { removeFolder, renameFolder } from '@/actions/folders';
import { useNavigate } from 'react-router-dom';

/**
 *
 * Folder schema:
 * {
 * _business: id,
 * _user: id,
 * _customerConversations: [id],
 * title: String
 * }
 */

export const DialogContentComponent = ({
	actionType,
	folder,
	setShowFolderOptionDialog,
}) => {
	const [newTitle, setNewTitle] = useState(folder.title);

	const dispatch = useDispatch();

	const deleteFolder = async () => {
		if (!folder._id) return;
		dispatch(removeFolder({ _folder: folder._id }));
		dispatch(
			deleteFolderFromAllCustomerConversations({ _folder: folder._id })
		);
		setShowFolderOptionDialog(false);
	};

	const renameFolderLocal = async () => {
		if (!folder._id) return;
		dispatch(
			renameFolder({ newFolderTitle: newTitle, _folder: folder._id })
		);
		setShowFolderOptionDialog(false);
	};

	// const handleKeyDown = (e) => {
	// 	if (e.key === 'Enter') {
	// 		e.preventDefault(); // Prevent default behavior of the Enter key
	// 		if (actionType === 'rename') {
	// 			renameFolderLocal();
	// 		} else if (actionType === 'delete') {
	// 			deleteFolder();
	// 		}
	// 	}
	// };

	// useEffect(() => {
	// 	document.addEventListener('keydown', handleKeyDown);
	// 	return () => {
	// 		document.removeEventListener('keydown', handleKeyDown);
	// 	};
	// }, [actionType]);

	if (actionType === 'rename') {
		return (
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Rename {folder.title}</DialogTitle>
				</DialogHeader>
				<Input
					value={newTitle}
					onChange={(e) => setNewTitle(e.target.value)}
				/>
				<DialogFooter>
					<DialogClose>
						<Button variant="ghost">Cancel</Button>
					</DialogClose>
					<Button onClick={renameFolderLocal}>Rename</Button>
				</DialogFooter>
			</DialogContent>
		);
	} else if (actionType === 'delete') {
		return (
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						Are you sure you want to delete {folder.title}?
					</DialogTitle>
				</DialogHeader>
				<DialogFooter>
					<DialogClose>
						<Button variant="ghost">Cancel</Button>
					</DialogClose>
					<Button onClick={deleteFolder}>Delete</Button>
				</DialogFooter>
			</DialogContent>
		);
	}
	return null;
};

const FolderSelector = ({
	folder,
	selectedType,
	setCustomerConversationAssignedType,
	isCollapsed,
	doAnyFoldersHaveUnreadConversations,
	conversationCount,
	deleteFolderFromAllCustomerConversations,
	doesCurrentFolderHaveUnreadConversations,
}) => {
	const [showFolderOptionsButton, setShowFolderOptionsButton] =
		useState(false);
	const [showFolderOptions, setShowFolderOptions] = useState(false);
	const [selectedFolder, setSelectedFolder] = useState(null);
	const [showFolderOptionDialog, setShowFolderOptionDialog] = useState(false);
	const [actionType, setActionType] = useState('');
	const auth = useSelector((state) => state.auth);
	const businessGroup = useSelector((state) => state.businessGroup);
	const folders = useSelector((state) => state.folders);

	const navigate = useNavigate();

	const currentBusinessGroup =
		folder && businessGroup.find((b) => b._id === folder._business);

	const handleHoverEvent = (event) => {
		if (showFolderOptionDialog) return;
		if (event.type === 'mouseenter') {
			setShowFolderOptionsButton(true);
		}
		if (!showFolderOptions) {
			if (event.type === 'mouseleave' || event.type === 'mouseout') {
				setShowFolderOptionsButton(false);
			}
		}
	};

	const handleRenameFolder = (e) => {
		e.preventDefault();
		setActionType('rename');
		setShowFolderOptionDialog(true);
	};

	const handleDeleteFolder = () => {
		setActionType('delete');
		setShowFolderOptionDialog(true);
	};

	const handleShowConversationsInFolder = (_folder) => {
		setSelectedFolder(_folder);
		setCustomerConversationAssignedType(`FOLDER_${_folder}`);
		navigate(`/folders/${_folder}`);
	};

	const getCurrentLocationForFolder = (_businessFolderBelongsTo) => {
		return businessGroup.find((b) => b._id === _businessFolderBelongsTo);
	};

	if (isCollapsed) {
		return (
			<Tooltip delayDuration={0}>
				<Select
					value={selectedFolder}
					onOpenChange={() =>
						!selectedType.includes('FOLDER_') &&
						setSelectedFolder(null)
					}
					onValueChange={handleShowConversationsInFolder}
				>
					<TooltipTrigger asChild>
						<SelectTrigger
							withoutArrow
							className={cn(
								selectedType.includes('FOLDER_')
									? 'bg-primary text-primary-foreground'
									: 'hover:bg-accent hover:text-accent-foreground border-none bg-transparent font-medium'
							)}
						>
							<Folders className="h-4 w-4" />
							{doAnyFoldersHaveUnreadConversations.length > 0 && (
								<span
									className={cn(
										'flex ml-0.5 h-2 w-2 rounded-full',
										selectedType.includes('FOLDER')
											? 'bg-primary-foreground'
											: 'bg-primary'
									)}
								/>
							)}
						</SelectTrigger>
					</TooltipTrigger>
					<TooltipContent side="right">All Folders</TooltipContent>
					<SelectContent>
						<SelectGroup>
							<SelectLabel>All Folders</SelectLabel>
							{folders.map((folder) => {
								const location = getCurrentLocationForFolder(
									folder._business
								);
								return (
									<SelectItem
										className="cursor-pointer"
										value={folder._id}
										key={folder._id}
									>
										<div className="flex items-center space-x-0.5">
											<p className="text-sm">
												{folder.title}
											</p>
											{doesCurrentFolderHaveUnreadConversations(
												folder._id
											) && (
												<span
													className={cn(
														'flex ml-0.5 h-2 w-2 rounded-full',
														'bg-primary'
													)}
												/>
											)}
										</div>
										<p className="text-xs text-muted-foreground">
											{auth.user.isEnterpriseEnabled &&
												location &&
												location.address?.split(',')[0]}
										</p>
									</SelectItem>
								);
							})}
						</SelectGroup>
					</SelectContent>
				</Select>
			</Tooltip>
		);
	}
	return (
		<div
			key={folder._id}
			className={cn(
				'flex py-2 w-full justify-between px-4 ml-1 cursor-pointer',
				'rounded-md items-center gap-x-1 hover:bg-accent hover:text-accent-foreground',
				selectedType === `FOLDER_${folder._id}` &&
					'bg-accent text-accent-foreground'
			)}
			onMouseEnter={handleHoverEvent}
			onMouseLeave={handleHoverEvent}
		>
			<div
				className="flex space-x-2 items-center w-full"
				onClick={() => {
					setCustomerConversationAssignedType(`FOLDER_${folder._id}`);
					navigate(`/folders/${folder._id}`);
				}}
			>
				<Folder className="size-4 text-muted-foreground" />
				<div>
					<div className="flex space-x-1 items-center">
						<p className="text-sm">{folder.title}</p>
						{doesCurrentFolderHaveUnreadConversations(
							folder._id
						) && (
							<span
								className={cn(
									'flex ml-0.5 h-2 w-2 rounded-full',

									'bg-primary'
								)}
							/>
						)}
					</div>
					<p className="text-xs text-muted-foreground">
						{auth.user.isEnterpriseEnabled &&
							currentBusinessGroup.address?.split(',')[0]}
					</p>
				</div>
			</div>
			<Dialog
				open={showFolderOptionDialog}
				onOpenChange={() =>
					setShowFolderOptionDialog(!showFolderOptionDialog)
				}
			>
				<div className="items-center flex">
					{!showFolderOptionsButton ? (
						<p className="text-sm opacity-50 size-4 pl-2">
							{conversationCount}
						</p>
					) : (
						<DropdownMenu
							open={showFolderOptions}
							onOpenChange={() => {
								setShowFolderOptions(!showFolderOptions);
							}}
						>
							<DropdownMenuTrigger asChild>
								<IconButton
									className="size-5"
									onClick={() => {
										setShowFolderOptionsButton(false);
										setShowFolderOptions(true);
									}}
								>
									<Ellipsis className="size-4 text-muted-foreground" />
								</IconButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								onFocusOutside={() =>
									setShowFolderOptionsButton(false)
								}
							>
								<DropdownMenuLabel>
									{conversationCount} items
								</DropdownMenuLabel>
								<DropdownMenuSeparator />

								<DialogTrigger asChild>
									<DropdownMenuItem
										onClick={handleRenameFolder}
									>
										Rename
									</DropdownMenuItem>
								</DialogTrigger>
								<DialogTrigger asChild>
									<DropdownMenuItem
										onSelect={handleDeleteFolder}
									>
										Delete
									</DropdownMenuItem>
								</DialogTrigger>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
				<DialogContentComponent
					actionType={actionType}
					folder={folder}
					setShowFolderOptionDialog={setShowFolderOptionDialog}
					deleteFolderFromAllCustomerConversations={
						deleteFolderFromAllCustomerConversations
					}
				/>
			</Dialog>
		</div>
	);
};

export default FolderSelector;
